import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import {
  getEventById,
  getAllTasks,
  createTask,
  updateTask,
  deleteTask,
  getAllEventRegistrations,
} from "../../Utils/apiServices";
import Button from "../../Components/Button";
import Card from "../../Components/Card";
import TextField from "../../Components/TextField";
import { Typography } from "../../Components/Typography";
import toast from "../../Utils/toastManager";

// Role display config: order and labels for registration containers (matches screenshot style)
const ROLE_DISPLAY_CONFIG = [
  { role: "mentor", label: "Registered Mentors", icon: "lucide:user-check" },
  { role: "volunteer", label: "Registered Volunteers", icon: "lucide:users" },
  { role: "unregistered_mentor", label: "Mentors / Advisors", icon: "lucide:user-check" },
  { role: "unregistered_founder", label: "Founders / Innovators / Students", icon: "lucide:lightbulb" },
  { role: "unregistered_investor", label: "Investors / Angels", icon: "lucide:trending-up" },
  { role: "unregistered_sponsor", label: "Sponsors / Brand Partners", icon: "lucide:handshake" },
  { role: "unregistered_ecosystem", label: "Ecosystem / Community Partners", icon: "lucide:globe" },
  { role: "_other", label: "Other", icon: "lucide:user" },
];

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [allEventRegistrations, setAllEventRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [taskFormData, setTaskFormData] = useState({
    title: "",
    task_details: "",
    number_of_hours: "",
    start_date: "",
    end_date: "",
    client_id: "",
  });

  const fetchEventDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getEventById(id);
      if (response.success && response.data?.data) {
        setEvent(
          Array.isArray(response.data.data)
            ? response.data.data[0]
            : response.data.data
        );
      } else {
        setError("Event not found");
      }
    } catch (err) {
      console.error("Error fetching event details:", err);
      setError(err.error || "Failed to load event details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await getAllTasks({ event_id: id });
      if (response.success && response.data?.data) {
        setTasks(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
    }
  }, [id]);

  const fetchEventRegistrations = useCallback(async () => {
    try {
      const response = await getAllEventRegistrations();
      if (response.success && (response.data?.data || response.data)) {
        const raw = response.data?.data ?? response.data;
        const registrations = Array.isArray(raw) ? raw : [];
        const forThisEvent = registrations.filter(
          (registration) => Number(registration.event_id) === Number(id)
        );
        setAllEventRegistrations(forThisEvent);
      }
    } catch (err) {
      console.error("Error fetching event registrations:", err);
    }
  }, [id]);

  // Group registrations by role for display; use _other for null/unknown role
  const registrationsByRole = React.useMemo(() => {
    const map = {};
    ROLE_DISPLAY_CONFIG.forEach(({ role }) => {
      map[role] = [];
    });
    allEventRegistrations.forEach((reg) => {
      const key = reg.role == null || reg.role === "" ? "_other" : reg.role;
      if (!map[key]) map[key] = [];
      map[key].push(reg);
    });
    return map;
  }, [allEventRegistrations]);

  // Volunteers only (for task assignment dropdown)
  const eventRegistrations = registrationsByRole.volunteer || [];

  useEffect(() => {
    fetchEventDetails();
    fetchTasks();
    fetchEventRegistrations();
  }, [fetchEventDetails, fetchTasks, fetchEventRegistrations]);

  const handleTaskInputChange = (field, value) => {
    setTaskFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetTaskForm = () => {
    setTaskFormData({
      title: "",
      task_details: "",
      number_of_hours: "",
      start_date: "",
      end_date: "",
      client_id: "",
    });
    setEditingTaskId(null);
    setShowTaskForm(false);
  };

  const handleEditTask = (task) => {
    setEditingTaskId(task.id);
    setShowTaskForm(true);
    
    // If task has email, find the registration by email to get the ID
    let registrationId = "";
    if (task.email) {
      const registration = eventRegistrations.find(
        (r) => {
          const regEmail = getRegistrationDisplayEmail(r);
          return regEmail === task.email;
        }
      );
      registrationId = registration?.id?.toString() || "";
    } else if (task.client_id) {
      // Backward compatibility: if task has client_id, use it
      registrationId = task.client_id.toString();
    }
    
    setTaskFormData({
      title: task.title || "",
      task_details: task.task_details || "",
      number_of_hours: task.number_of_hours?.toString() || "",
      start_date: task.start_date
        ? new Date(task.start_date).toISOString().split("T")[0]
        : "",
      end_date: task.end_date
        ? new Date(task.end_date).toISOString().split("T")[0]
        : "",
      client_id: registrationId,
    });
  };

  const handleSaveTask = async () => {
    try {
      // Find the registration by selected ID to get the email
      const selectedRegistration = eventRegistrations.find(
        (r) => r.id === parseInt(taskFormData.client_id) || r.id === taskFormData.client_id
      );
      
      const registrationEmail = selectedRegistration ? getRegistrationDisplayEmail(selectedRegistration) : null;
      
      if (!selectedRegistration || !registrationEmail) {
        toast.warning("Please select a valid volunteer");
        return;
      }

      const taskData = {
        title: taskFormData.title,
        task_details: taskFormData.task_details,
        number_of_hours: parseFloat(taskFormData.number_of_hours),
        start_date: taskFormData.start_date,
        end_date: taskFormData.end_date,
        event_id: parseInt(id),
        email: registrationEmail,
      };

      let response;
      if (editingTaskId) {
        response = await updateTask(editingTaskId, taskData);
      } else {
        response = await createTask(taskData);
      }

      if (response.success) {
        await fetchTasks();
        resetTaskForm();
      } else {
        toast.error(
          `Failed to ${editingTaskId ? "update" : "create"} task: ${
            response.error || "Unknown error"
          }`
        );
      }
    } catch (error) {
      console.error(`Error saving task:`, error);
      toast.error(
        `An error occurred while ${editingTaskId ? "updating" : "creating"} the task`
      );
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) {
      return;
    }

    try {
      const response = await deleteTask(taskId);
      if (response.success) {
        await fetchTasks();
      } else {
        toast.error("Failed to delete task: " + (response.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("An error occurred while deleting the task");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatEventType = (eventType) => {
    if (!eventType) return "-";
    return String(eventType)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getRegistrationDisplayName = (registration) => {
    // Check if registration has user object
    if (registration.user) {
      const fullName = `${registration.user.first_name || ""} ${registration.user.last_name || ""}`.trim();
      return fullName || registration.user.email || "-";
    }
    // Fallback to name or email fields
    return registration.name || registration.email || "-";
  };

  const getRegistrationDisplayEmail = (registration) => {
    // Check if registration has user object
    if (registration.user) {
      return registration.user.email || registration.email || null;
    }
    return registration.email || null;
  };

  const getVolunteerName = (clientId, email) => {
    // First try to find by email if provided
    if (email) {
      const registrationByEmail = eventRegistrations.find((r) => {
        const regEmail = getRegistrationDisplayEmail(r);
        return regEmail === email;
      });
      if (registrationByEmail) {
        return getRegistrationDisplayName(registrationByEmail);
      }
    }
    // Fallback to finding by ID (backward compatibility)
    if (clientId) {
      const registration = eventRegistrations.find((r) => r.id === parseInt(clientId) || r.id === clientId);
      if (registration) {
        return getRegistrationDisplayName(registration);
      }
    }
    return "-";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Typography as="p" className="text-tertiary-600">
          Loading event details...
        </Typography>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <Typography as="p" className="text-red-600">
          {error || "Event not found"}
        </Typography>
        <Button variant="secondary" onClick={() => navigate("/events")}>
          Back to Events
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            onClick={() => navigate("/events")}
            startIcon={<Icon icon="lucide:arrow-left" className="h-4 w-4" />}
          >
            Back
          </Button>
          <Typography as="h1" className="text-2xl font-bold text-primary-900">
            Event Details
          </Typography>
        </div>
      </div>

      {/* Event Information */}
      <Card className="shadow-sm border border-slate-200">
        <div className="p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-lg">
              <Icon icon="lucide:calendar" className="h-8 w-8 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Typography as="h2" className="text-2xl font-bold text-primary-900">
                  {event.title || "-"}
                </Typography>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                    event.is_active
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {event.is_active ? "Active" : "Inactive"}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 capitalize">
                  {event.event_type || "-"}
                </span>
              </div>
              {event.description && (
                <div 
                  className="text-base text-tertiary-600 mt-2 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: event.description }}
                />
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-slate-200">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                <Icon icon="lucide:calendar-clock" className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <Typography as="p" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider mb-1">
                  Start Date & Time
                </Typography>
                <Typography as="p" className="text-base font-medium text-primary-900">
                  {formatDate(event.start_at)}
                </Typography>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                <Icon icon="lucide:calendar-x" className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <Typography as="p" className="text-xs font-semibold text-tertiary-600 uppercase tracking-wider mb-1">
                  End Date & Time
                </Typography>
                <Typography as="p" className="text-base font-medium text-primary-900">
                  {formatDate(event.end_at)}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Registrations by role – one container per role (screenshot style) */}
      {ROLE_DISPLAY_CONFIG.map(({ role, label, icon }) => {
        const list = registrationsByRole[role] || [];
        const count = list.length;
        return (
          <div key={role || "_other"} className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Icon icon={icon} className="h-5 w-5 text-blue-600" />
              </div>
              <Typography as="h2" className="text-xl font-bold text-primary-900">
                {label} ({count})
              </Typography>
            </div>
            <Card className="border border-slate-200 shadow-sm overflow-hidden">
              {count === 0 ? (
                <div className="p-12 text-center">
                  <div className="h-16 w-16 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center mx-auto mb-4">
                    <Icon icon={icon} className="h-8 w-8 text-slate-400" />
                  </div>
                  <Typography as="p" className="text-tertiary-600 text-base">
                    No {label.toLowerCase().replace(/^registered\s+/, "")} registered for this event yet.
                  </Typography>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                          Name
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                          Email
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                          Event
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                          Event Type
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                          Dates
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                          Expertise
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                          Interest
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {list.map((registration) => {
                        const ev = registration.event;
                        const displayName = getRegistrationDisplayName(registration);
                        const displayEmail = getRegistrationDisplayEmail(registration);
                        const primaryExpertise = Array.isArray(registration.primary_expertise) ? registration.primary_expertise : [];
                        const preferredRoles = Array.isArray(registration.preferred_roles) ? registration.preferred_roles : [];
                        return (
                          <tr key={registration.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3 text-sm font-medium text-primary-900 whitespace-nowrap">
                              {displayName}
                            </td>
                            <td className="px-4 py-3 text-sm text-tertiary-600 whitespace-nowrap">
                              {displayEmail || "—"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {registration.status ? (
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                  registration.status === "approved" ? "bg-green-100 text-green-700" :
                                  registration.status === "registered" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
                                }`}>
                                  {registration.status}
                                </span>
                              ) : "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-primary-900 max-w-[200px]">
                              {ev?.title || "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-tertiary-600 whitespace-nowrap">
                              {ev?.event_type ? formatEventType(ev.event_type) : "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-tertiary-600 whitespace-nowrap">
                              {ev?.start_at && ev?.end_at
                                ? `${formatDateOnly(ev.start_at)} – ${formatDateOnly(ev.end_at)}`
                                : "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-tertiary-600">
                              {primaryExpertise.length > 0 ? (
                                <span className="inline-flex flex-wrap gap-1">
                                  {primaryExpertise.map((item) => (
                                    <span key={item} className="inline-flex px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs">
                                      {item}
                                    </span>
                                  ))}
                                </span>
                              ) : "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-tertiary-600">
                              {preferredRoles.length > 0 ? (
                                <span className="inline-flex flex-wrap gap-1">
                                  {preferredRoles.map((item) => (
                                    <span key={item} className="inline-flex px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs">
                                      {item}
                                    </span>
                                  ))}
                                </span>
                              ) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        );
      })}

      {/* Tasks Section */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <Icon icon="lucide:check-square" className="h-5 w-5 text-purple-600" />
          </div>
          <Typography as="h2" className="text-xl font-bold text-primary-900">
            Tasks ({tasks.length})
          </Typography>
        </div>
        <Button
          onClick={() => {
            resetTaskForm();
            setShowTaskForm(true);
          }}
          startIcon={<Icon icon="lucide:plus" className="h-4 w-4" />}
        >
          Add Task
        </Button>
      </div>

      {/* Task Form */}
      {showTaskForm && (
        <Card className="border border-slate-200 shadow-sm">
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3 pb-4 border-b border-slate-200">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Icon icon={editingTaskId ? "lucide:edit" : "lucide:plus-circle"} className="h-5 w-5 text-blue-600" />
              </div>
              <Typography
                as="h3"
                className="text-lg font-bold text-primary-900"
              >
                {editingTaskId ? "Edit Task" : "Create New Task"}
              </Typography>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField
                name="title"
                label="Task Title"
                type="text"
                value={taskFormData.title}
                onChange={(e) =>
                  handleTaskInputChange("title", e.target.value)
                }
                required
                placeholder="Enter task title"
              />
              <div>
                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                  Assign to Volunteer <span className="text-red-500">*</span>
                </label>
                <select
                  name="client_id"
                  value={taskFormData.client_id}
                  onChange={(e) =>
                    handleTaskInputChange("client_id", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a volunteer</option>
                  {eventRegistrations.map((registration) => {
                    const displayName = getRegistrationDisplayName(registration);
                    const displayEmail = getRegistrationDisplayEmail(registration);
                    return (
                      <option key={registration.id} value={registration.id}>
                        {displayName}
                        {displayEmail && ` (${displayEmail})`}
                      </option>
                    );
                  })}
                </select>
              </div>
              <TextField
                name="number_of_hours"
                label="Number of Hours Needed"
                type="number"
                step="0.01"
                min="0"
                value={taskFormData.number_of_hours}
                onChange={(e) =>
                  handleTaskInputChange("number_of_hours", e.target.value)
                }
                required
                placeholder="Enter number of hours needed"
              />
              <TextField
                name="start_date"
                label="Start Date"
                type="date"
                value={taskFormData.start_date}
                onChange={(e) =>
                  handleTaskInputChange("start_date", e.target.value)
                }
                required
              />
              <TextField
                name="end_date"
                label="End Date"
                type="date"
                value={taskFormData.end_date}
                onChange={(e) =>
                  handleTaskInputChange("end_date", e.target.value)
                }
                required
              />
            </div>
            <div>
              <TextField
                name="task_details"
                label="Task Details"
                type="text"
                multiline
                rows={4}
                value={taskFormData.task_details}
                onChange={(e) =>
                  handleTaskInputChange("task_details", e.target.value)
                }
                required
                placeholder="Enter task details"
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="secondary" onClick={resetTaskForm}>
                Cancel
              </Button>
              <Button onClick={handleSaveTask}>
                {editingTaskId ? "Update Task" : "Create Task"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Tasks List */}
      <Card className="border border-slate-200 shadow-sm overflow-hidden">
        {tasks.length === 0 ? (
          <div className="p-12 text-center">
            <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Icon icon="lucide:check-square" className="h-8 w-8 text-slate-400" />
            </div>
            <Typography as="p" className="text-tertiary-600 text-base">
              No tasks found for this event.
            </Typography>
            <Typography as="p" className="text-tertiary-500 text-sm mt-1">
              Click "Add Task" to create a new task.
            </Typography>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-primary-900 uppercase tracking-wider">
                    Task Title
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-primary-900 uppercase tracking-wider">
                    Task Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-primary-900 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-primary-900 uppercase tracking-wider">
                    Hours
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-primary-900 uppercase tracking-wider">
                    Start Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-primary-900 uppercase tracking-wider">
                    End Date
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-primary-900 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {tasks.map((task, index) => (
                  <tr 
                    key={task.id} 
                    className="hover:bg-blue-50 transition-colors group"
                  >
                    <td className="px-6 py-4">
                      <Typography
                        as="span"
                        className="text-sm font-medium text-primary-900"
                      >
                        {task.title || "-"}
                      </Typography>
                    </td>
                    <td className="px-6 py-4">
                      <Typography
                        as="span"
                        className="text-sm font-medium text-primary-900 line-clamp-2"
                        title={task.task_details}
                      >
                        {task.task_details || "-"}
                      </Typography>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-400 to-purple-500 flex items-center justify-center text-white text-xs font-semibold">
                          {getVolunteerName(task.client_id, task.email).charAt(0).toUpperCase()}
                        </div>
                        <Typography as="span" className="text-sm font-medium text-primary-900">
                          {getVolunteerName(task.client_id, task.email)}
                        </Typography>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                        <Icon icon="lucide:clock" className="h-3 w-3 mr-1" />
                        {task.number_of_hours ? `${task.number_of_hours} hrs` : "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Icon icon="lucide:calendar" className="h-4 w-4 text-slate-400" />
                        <Typography as="span" className="text-sm font-medium text-primary-900">
                          {formatDateOnly(task.start_date)}
                        </Typography>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Icon icon="lucide:calendar-check" className="h-4 w-4 text-slate-400" />
                        <Typography as="span" className="text-sm font-medium text-primary-900">
                          {formatDateOnly(task.end_date)}
                        </Typography>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => handleEditTask(task)}
                          startIcon={<Icon icon="lucide:edit" className="h-4 w-4" />}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => handleDeleteTask(task.id)}
                          startIcon={
                            <Icon icon="lucide:trash-2" className="h-4 w-4" />
                          }
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default EventDetail;
